import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import DataTable from "@/components/admin/DataTable";
import DetailLayout from "@/components/admin/DetailLayout";
import StatusBadge from "@/components/admin/StatusBadge";
import Timeline from "@/components/admin/Timeline";
import EmptyState from "@/components/admin/EmptyState";
import ConfirmActionDialog from "@/components/admin/ConfirmActionDialog";
import CustomerNotesTab from "@/components/admin/CustomerNotesTab";
import { Button } from "@/components/ui/button";
import {
  FileText, Image as ImageIcon, Palette, CreditCard, Download as DLIcon,
  MessageSquare, StickyNote, KeyRound, UserX, UserCheck, Trash2, Undo2, Unlock,
} from "lucide-react";
import { toast } from "sonner";

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-AU");
}

function money(n) { return `$${(n ?? 0).toFixed(2)}`; }

function statusTone(s) {
  if (s === "active") return "green";
  if (s === "disabled") return "amber";
  if (s === "deleted") return "red";
  return "grey";
}

const PlaceholderHint = ({ milestone }) => (
  <div className="text-xs text-brand-muted mt-1">
    Detail page available in {milestone}.
  </div>
);

function TabPaginated({ endpoint, columns, testId, emptyTitle, emptyCaption, emptyIcon, placeholderHint, refreshKey }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    api.get(`${endpoint}?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [endpoint, page, pageSize, refreshKey]);

  return (
    <div>
      <DataTable
        testId={testId}
        columns={columns}
        rows={data.items}
        total={data.total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        loading={loading}
        empty={(
          <EmptyState icon={emptyIcon} title={emptyTitle} caption={emptyCaption} testId={`${testId}-empty`} />
        )}
      />
      {placeholderHint}
    </div>
  );
}

const ACTIONS = {
  resetPassword: {
    title: "Send password reset email",
    description: "The customer will receive a one-time reset link via email. Any previous unused reset link is invalidated.",
    confirmLabel: "Send reset email",
    endpoint: (id) => `/admin/customers/${id}/password-reset`,
    successMsg: "Reset email triggered",
    body: () => ({}),
  },
  disable: {
    title: "Disable this customer account",
    description: "The customer will be blocked from logging in until re-enabled. Their data is preserved.",
    confirmLabel: "Disable account",
    destructive: true,
    endpoint: (id) => `/admin/customers/${id}/disable`,
    successMsg: "Account disabled",
    body: (reason) => ({ reason }),
  },
  enable: {
    title: "Re-enable this customer",
    description: "The customer will be able to log in again immediately.",
    confirmLabel: "Re-enable account",
    endpoint: (id) => `/admin/customers/${id}/enable`,
    successMsg: "Account re-enabled",
    body: () => ({}),
  },
  softDelete: {
    title: "Soft-delete this customer (Owner only)",
    description: "Login is blocked and the customer is removed from default lists. Restoration is possible from the same screen. No data is permanently removed.",
    confirmLabel: "Soft-delete customer",
    destructive: true,
    endpoint: (id) => `/admin/customers/${id}/soft-delete`,
    successMsg: "Customer soft-deleted",
    body: (reason) => ({ reason }),
  },
  restore: {
    title: "Restore this customer (Owner only)",
    description: "The customer becomes active again and can log in.",
    confirmLabel: "Restore customer",
    endpoint: (id) => `/admin/customers/${id}/restore`,
    successMsg: "Customer restored",
    body: () => ({}),
  },
  markInternal: {
    title: "Mark customer as internal (Owner only)",
    description: "This customer will be excluded from operational metrics (customer counts, project counts, render counts). Stripe revenue is NEVER affected. Reverse by clicking 'Unmark internal'.",
    confirmLabel: "Mark internal",
    reasonRequired: true,
    reasonMinLength: 10,
    reasonLabel: "Reason (≥10 characters)",
    endpoint: (id) => `/admin/customers/${id}/mark-internal`,
    successMsg: "Customer marked as internal",
    body: (reason) => ({ reason }),
  },
  unmarkInternal: {
    title: "Unmark internal flag (Owner only)",
    description: "The customer's data will once again count towards operational metrics.",
    confirmLabel: "Unmark internal",
    endpoint: (id) => `/admin/customers/${id}/unmark-internal`,
    successMsg: "Customer unmarked",
    body: () => ({}),
  },
  hardDelete: {
    title: "Hard-delete this customer (Owner only · IRREVERSIBLE)",
    description: "This permanently removes the customer and ALL their data — projects, renders, notes, support tickets — and CANNOT be undone. Type the customer's email exactly to confirm. Only allowed after the customer has been soft-deleted for 30 days.",
    confirmLabel: "Permanently delete",
    destructive: true,
    reasonRequired: true,
    reasonMinLength: 10,
    reasonLabel: "Reason (≥10 characters)",
    extraConfirm: true,
    extraConfirmField: "email",
    method: "DELETE",
    endpoint: (id) => `/admin/customers/${id}/hard`,
    successMsg: "Customer permanently deleted",
    body: (reason, confirm) => ({ reason, confirm }),
  },
};

export default function AdminCustomerDetail({ customerId, onBack }) {
  const { user: actor } = useAuth();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("profile");
  const [err, setErr] = useState("");
  const [dlg, setDlg] = useState(null);     // {kind, projectId?} | null
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const reload = useCallback(() => {
    api.get(`/admin/customers/${customerId}`)
      .then((r) => { setC(r.data); setErr(""); })
      .catch((e) => setErr(e.message || "Failed to load"));
  }, [customerId]);

  useEffect(() => { reload(); }, [reload]);

  const onConfirmAction = async (reason) => {
    if (!dlg) return;
    if (dlg.kind === "unlock") {
      setBusy(true);
      try {
        await api.post(
          `/admin/customers/${customerId}/projects/${dlg.projectId}/unlock`,
          { reason },
        );
        toast.success("Project unlocked");
        setDlg(null);
        setRefreshKey((k) => k + 1);
        reload();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to unlock");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (dlg.kind === "hardDelete") {
      // Owner-only · IRREVERSIBLE. Require email-typed-back confirmation.
      const expected = (c?.email || "").trim();
      const confirm = window.prompt(
        `IRREVERSIBLE: Type the customer's email to confirm permanent deletion.\n\nExpected: ${expected}`
      );
      if (!confirm) return;
      setBusy(true);
      try {
        await api.delete(`/admin/customers/${customerId}/hard`, {
          data: { confirm: confirm.trim(), reason },
        });
        toast.success("Customer permanently deleted");
        setDlg(null);
        // Bounce back to list since the customer no longer exists
        if (typeof onBack === "function") onBack();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Hard-delete failed");
      } finally {
        setBusy(false);
      }
      return;
    }
    const cfg = ACTIONS[dlg.kind];
    if (!cfg) return;
    setBusy(true);
    try {
      await api.post(cfg.endpoint(customerId), cfg.body(reason));
      toast.success(cfg.successMsg);
      setDlg(null);
      reload();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return <div className="text-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3">{err}</div>;
  }
  if (!c) return <div className="text-sm text-brand-muted">Loading customer…</div>;

  const L = c.lifetime || {};
  const isDisabled = c.status === "disabled";
  const isDeleted = c.status === "deleted";
  const isOwnerActor = !!actor?.is_owner;

  const tabs = [
    { key: "profile", label: "Profile" },
    { key: "projects", label: "Projects", count: L.projects },
    { key: "renders", label: "Renders", count: L.renders },
    { key: "schemes", label: "Schemes", count: L.schemes },
    { key: "payments", label: "Payments", count: L.payments },
    { key: "notes", label: "Notes" },
    { key: "downloads", label: "Downloads", count: L.downloads },
    { key: "support", label: "Support", count: L.tickets },
    { key: "timeline", label: "Timeline", count: L.events },
  ];

  const actions = (
    <>
      <Button
        data-testid="action-password-reset"
        variant="outline"
        size="sm"
        onClick={() => setDlg({ kind: "resetPassword" })}
        disabled={isDisabled || isDeleted}
        title={isDisabled || isDeleted ? "Account is not active" : "Send password reset email"}
      >
        <KeyRound className="w-4 h-4 mr-1" /> Send password reset
      </Button>
      {isDisabled ? (
        <Button
          data-testid="action-enable"
          variant="outline"
          size="sm"
          onClick={() => setDlg({ kind: "enable" })}
          disabled={isDeleted}
        >
          <UserCheck className="w-4 h-4 mr-1" /> Re-enable
        </Button>
      ) : (
        <Button
          data-testid="action-disable"
          variant="outline"
          size="sm"
          onClick={() => setDlg({ kind: "disable" })}
          disabled={isDeleted}
        >
          <UserX className="w-4 h-4 mr-1" /> Disable
        </Button>
      )}
      {isOwnerActor && (
        isDeleted ? (
          <Button
            data-testid="action-restore"
            variant="outline"
            size="sm"
            onClick={() => setDlg({ kind: "restore" })}
          >
            <Undo2 className="w-4 h-4 mr-1" /> Restore
          </Button>
        ) : (
          <Button
            data-testid="action-soft-delete"
            variant="outline"
            size="sm"
            onClick={() => setDlg({ kind: "softDelete" })}
          >
            <Trash2 className="w-4 h-4 mr-1" /> Soft-delete
          </Button>
        )
      )}
      {isOwnerActor && (
        <Button
          data-testid={c.is_internal ? "action-unmark-internal" : "action-mark-internal"}
          variant="outline"
          size="sm"
          onClick={() => setDlg({ kind: c.is_internal ? "unmarkInternal" : "markInternal" })}
        >
          {c.is_internal ? "Unmark internal" : "Mark internal"}
        </Button>
      )}
      {isOwnerActor && isDeleted && (
        <Button
          data-testid="action-hard-delete"
          variant="destructive"
          size="sm"
          onClick={() => setDlg({ kind: "hardDelete" })}
        >
          <Trash2 className="w-4 h-4 mr-1" /> Hard delete
        </Button>
      )}
    </>
  );

  let banner = null;
  if (isDeleted) {
    banner = (
      <div className="text-xs px-3 py-2 bg-red-50 border border-red-200 text-red-700" data-testid="customer-banner-deleted">
        Account is soft-deleted. Login is blocked. Owner can restore via the Restore button.
      </div>
    );
  } else if (isDisabled) {
    banner = (
      <div className="text-xs px-3 py-2 bg-amber-50 border border-amber-200 text-amber-700" data-testid="customer-banner-disabled">
        Account is disabled. Login is blocked until an admin re-enables it.
      </div>
    );
  }

  const dlgCfg = dlg && dlg.kind !== "unlock" ? ACTIONS[dlg.kind] : null;

  return (
    <>
      <DetailLayout
        testId="admin-customer-detail"
        onBack={onBack}
        backLabel="← Back to customers"
        title={c.name || "(no name)"}
        status={<StatusBadge tone={statusTone(c.status)} label={c.status} testId="customer-status" />}
        subText={
          <>
            {c.email} · joined {c.created_at ? new Date(c.created_at).toLocaleDateString("en-AU") : "—"} · last login {relTime(c.last_login_at)}
          </>
        }
        actions={actions}
        banner={banner}
        tabs={tabs}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === "profile" && (
          <div data-testid="customer-tab-profile" className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-brand-linen p-6">
              <div className="overline mb-4">Customer information</div>
              <Row label="Name" value={c.name} />
              <Row label="Email" value={c.email} mono />
              <Row label="Phone" value={c.phone || "—"} />
              <Row label="Role" value={c.role} />
              <Row label="Account status" value={<StatusBadge tone={statusTone(c.status)} label={c.status} />} />
              <Row label="Registered" value={c.created_at ? new Date(c.created_at).toLocaleString("en-AU") : "—"} />
              <Row label="Last login" value={relTime(c.last_login_at)} />
            </div>
            <div className="bg-white border border-brand-linen p-6">
              <div className="overline mb-4">Lifetime activity</div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <Stat label="Projects" value={L.projects} />
                <Stat label="Renders" value={L.renders} />
                <Stat label="Schemes" value={L.schemes} />
                <Stat label="Payments" value={L.payments} />
                <Stat label="Spend" value={money(L.spend_aud)} />
                <Stat label="Tickets" value={L.tickets} />
                <Stat label="Downloads" value={L.downloads} />
                <Stat label="Events" value={L.events} />
              </div>
            </div>
          </div>
        )}

        {tab === "projects" && (
          <div data-testid="customer-tab-projects">
            <TabPaginated
              endpoint={`/admin/customers/${customerId}/projects`}
              testId="customer-projects-table"
              refreshKey={refreshKey}
              emptyIcon={FileText} emptyTitle="No projects yet" emptyCaption="This customer hasn't created any projects."
              placeholderHint={<PlaceholderHint milestone="M3" />}
              columns={[
                { key: "title", label: "Title", render: (r) => r.title || "(untitled)" },
                { key: "unlocked", label: "Status", render: (r) => (
                  <StatusBadge tone={r.unlocked ? "green" : "grey"} label={r.unlocked ? "UNLOCKED" : "LOCKED"} />
                )},
                { key: "renders_count", label: "Renders", className: "text-right" },
                { key: "updated_at", label: "Updated", render: (r) => relTime(r.updated_at) },
                { key: "_actions", label: "", render: (r) => (
                  r.unlocked ? null : (
                    <button
                      data-testid={`project-unlock-${r.id}`}
                      onClick={() => setDlg({ kind: "unlock", projectId: r.id })}
                      className="inline-flex items-center gap-1 text-xs text-brand-teal hover:underline"
                      title="Manually unlock without payment"
                      disabled={isDeleted}
                    >
                      <Unlock className="w-3.5 h-3.5" /> Unlock
                    </button>
                  )
                )},
              ]}
            />
          </div>
        )}

        {tab === "renders" && (
          <div data-testid="customer-tab-renders">
            <TabPaginated
              endpoint={`/admin/customers/${customerId}/renders`}
              testId="customer-renders-table"
              emptyIcon={ImageIcon} emptyTitle="No renders yet" emptyCaption="This customer hasn't generated any renders."
              placeholderHint={<PlaceholderHint milestone="M3" />}
              columns={[
                { key: "project_title", label: "Project", render: (r) => r.project_title || "—" },
                { key: "surface", label: "Surface" },
                { key: "color_name", label: "Colour", render: (r) => (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-4 h-4 border border-brand-linen" style={{ background: r.color_hex || "#ccc" }} />
                    {r.color_name || "—"}
                  </span>
                )},
                { key: "status", label: "Status", render: (r) => (
                  <StatusBadge tone={r.status === "ready" ? "green" : r.status === "failed" ? "red" : "amber"} label={r.status} />
                )},
                { key: "created_at", label: "Created", render: (r) => relTime(r.created_at) },
              ]}
            />
          </div>
        )}

        {tab === "schemes" && (
          <div data-testid="customer-tab-schemes">
            <TabPaginated
              endpoint={`/admin/customers/${customerId}/schemes`}
              testId="customer-schemes-table"
              emptyIcon={Palette} emptyTitle="No schemes yet" emptyCaption="This customer hasn't saved any colour schemes."
              placeholderHint={<PlaceholderHint milestone="M3" />}
              columns={[
                { key: "name", label: "Name", render: (r) => r.name || "(untitled)" },
                { key: "project_title", label: "Project", render: (r) => r.project_title || "—" },
                { key: "created_at", label: "Created", render: (r) => relTime(r.created_at) },
              ]}
            />
          </div>
        )}

        {tab === "payments" && (
          <div data-testid="customer-tab-payments">
            <TabPaginated
              endpoint={`/admin/customers/${customerId}/payments`}
              testId="customer-payments-table"
              emptyIcon={CreditCard} emptyTitle="No payments yet" emptyCaption="This customer hasn't made any payments."
              placeholderHint={<PlaceholderHint milestone="M4" />}
              columns={[
                { key: "amount_aud", label: "Amount", className: "text-right", render: (r) => money(r.amount_aud) },
                { key: "payment_status", label: "Status", render: (r) => (
                  <StatusBadge tone={r.payment_status === "paid" ? "green" : "amber"} label={r.payment_status || "—"} />
                )},
                { key: "project_title", label: "Project", render: (r) => r.project_title || "—" },
                { key: "tx_id", label: "Stripe ref", render: (r) => (
                  <span className="font-mono text-xs">{(r.tx_id || "").slice(0, 18)}…</span>
                )},
                { key: "created_at", label: "Date", render: (r) => relTime(r.created_at) },
              ]}
            />
          </div>
        )}

        {tab === "notes" && (
          <CustomerNotesTab customerId={customerId} />
        )}

        {tab === "downloads" && (
          <div data-testid="customer-tab-downloads">
            <TabPaginated
              endpoint={`/admin/customers/${customerId}/downloads`}
              testId="customer-downloads-table"
              emptyIcon={DLIcon} emptyTitle="No downloads yet" emptyCaption="No downloads recorded — events appear here from M2A onward."
              placeholderHint={null}
              columns={[
                { key: "kind", label: "Type", render: (r) => <span className="font-mono text-xs">{r.kind}</span> },
                { key: "summary", label: "Detail", render: (r) => r.summary },
                { key: "created_at", label: "When", render: (r) => relTime(r.created_at) },
              ]}
            />
          </div>
        )}

        {tab === "support" && (
          <div data-testid="customer-tab-support">
            <TabPaginated
              endpoint={`/admin/customers/${customerId}/support`}
              testId="customer-support-table"
              emptyIcon={MessageSquare} emptyTitle="No support tickets" emptyCaption="This customer hasn't raised any support requests."
              placeholderHint={<PlaceholderHint milestone="M5" />}
              columns={[
                { key: "subject", label: "Subject", render: (r) => r.subject || "(no subject)" },
                { key: "status", label: "Status", render: (r) => (
                  <StatusBadge tone={r.status === "resolved" ? "green" : r.status === "new" ? "amber" : "grey"} label={r.status || "new"} />
                )},
                { key: "created_at", label: "Created", render: (r) => relTime(r.created_at) },
              ]}
            />
          </div>
        )}

        {tab === "timeline" && (
          <div data-testid="customer-tab-timeline">
            <Timeline
              endpoint={`/admin/customers/${customerId}/timeline`}
              testId="customer-timeline"
              refreshKey={refreshKey}
            />
          </div>
        )}
      </DetailLayout>

      {dlg && dlg.kind === "unlock" && (
        <ConfirmActionDialog
          open
          onClose={() => setDlg(null)}
          onConfirm={onConfirmAction}
          title="Manually unlock this project"
          description="Sets the project to unlocked without a Stripe payment. The action is logged in the audit log and timeline. No payment transaction is recorded."
          confirmLabel="Unlock project"
          reasonRequired
          reasonLabel="Reason (required)"
          reasonPlaceholder="e.g. Customer paid offline / VIP comp / refund replacement"
          busy={busy}
          testId="dialog-unlock"
        />
      )}
      {dlgCfg && (
        <ConfirmActionDialog
          open
          onClose={() => setDlg(null)}
          onConfirm={onConfirmAction}
          title={dlgCfg.title}
          description={dlgCfg.description}
          confirmLabel={dlgCfg.confirmLabel}
          destructive={dlgCfg.destructive}
          busy={busy}
          testId={`dialog-${dlg.kind}`}
        />
      )}
    </>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2 border-b border-brand-linen/40 last:border-0">
      <div className="text-xs uppercase tracking-wider text-brand-muted">{label}</div>
      <div className={`text-sm text-brand-ink ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-serif text-2xl text-brand-ink">{value ?? 0}</div>
      <div className="text-xs text-brand-muted">{label}</div>
    </div>
  );
}
